import React from 'react';
import { Layout, Menu, Button, Space, Avatar, Dropdown } from 'antd';
import {
  HomeOutlined,
  SearchOutlined,
  HeartOutlined,
  UserOutlined,
  LoginOutlined,
  LogoutOutlined,
  TrophyOutlined,
  StarOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  GlobalOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout } from '../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';

const { Header } = Layout;

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: t('user.profile'),
        onClick: () => navigate('/profile'),
      },
      {
        key: 'favorites',
        icon: <HeartOutlined />,
        label: t('user.favorites'),
        onClick: () => navigate('/favorites'),
      },
      {
        key: 'history',
        icon: <ClockCircleOutlined />,
        label: t('user.history'),
        onClick: () => navigate('/history'),
      },
      {
        key: 'my-ratings',
        icon: <StarOutlined />,
        label: t('user.myRatings'),
        onClick: () => navigate('/my-ratings'),
      },
      {
        key: 'my-reviews',
        icon: <DashboardOutlined />,
        label: t('user.myReviews'),
        onClick: () => navigate('/my-reviews'),
      },
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: t('user.dashboard'),
        onClick: () => navigate('/dashboard'),
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('user.logout'),
        onClick: handleLogout,
      },
    ],
  };

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/">{t('nav.home')}</Link>,
    },
    {
      key: '/brands',
      icon: <SearchOutlined />,
      label: <Link to="/brands">{t('nav.brands')}</Link>,
    },
    {
      key: '/rankings',
      icon: <BarChartOutlined />,
      label: <Link to="/rankings">{t('nav.rankings')}</Link>,
    },
    {
      key: '/world-map',
      icon: <GlobalOutlined />,
      label: <Link to="/world-map">{t('nav.worldMap')}</Link>,
    },
    {
      key: '/purchase-channels',
      icon: <ShoppingOutlined />,
      label: <Link to="/purchase-channels">购买渠道</Link>,
    },
    {
      key: '/recommendations',
      icon: <UserOutlined />,
      label: <Link to="/recommendations">{t('nav.recommendations')}</Link>,
    },
    {
      key: '/about',
      icon: <GlobalOutlined />,
      label: <Link to="/about">{t('nav.about')}</Link>,
    },
  ];

  return (
    <Header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        padding: '0 24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', marginRight: 32 }}
        >
          <img
            src="/logo-light.svg"
            alt={t('brand')}
            height={28}
            style={{ display: 'block', marginRight: 8 }}
          />
          <span
            style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: 'var(--app-primary-color)'
            }}
          >
            {t('brand')}
          </span>
        </Link>
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{ border: 'none', minWidth: '200px' }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ThemeSwitcher />
        <LanguageSwitcher />
        {isAuthenticated && user ? (
          <Dropdown menu={userMenu} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }} size="small">
              <Avatar icon={<UserOutlined />} />
              <span>{user.username || user.email}</span>
            </Space>
          </Dropdown>
        ) : (
          <Space size="small">
            <Button
              type="text"
              icon={<LoginOutlined />}
              onClick={() => navigate('/login')}
            >
              {t('user.login')}
            </Button>
            <Button type="primary" onClick={() => navigate('/register')}>
              {t('user.register')}
            </Button>
          </Space>
        )}
      </div>
    </Header>
  );
};

export default Navbar;
